import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import tiredStaff from '../assets/tired-staff.jpg';

const ProblemSection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const problems = [
    { 
      text: 'High claim denial rates impacting small practice revenue',
      link: '/reduce-claim-denials-healthcare-billing',
      linkText: 'Reduce Claim Denials'
    },
    { 
      text: 'Limited billing staff and expertise for independent physicians',
      link: '/revenue-cycle-management-independent-physicians',
      linkText: 'Independent Physician RCM'
    },
    { 
      text: 'Complex billing requirements for multispecialty practices',
      link: '/revenue-cycle-management-multispecialty-practices',
      linkText: 'Multispecialty Solutions'
    },
    { 
      text: 'Time-consuming manual billing processes reducing patient care time',
      link: '/medical-billing-outsourcing-small-practices',
      linkText: 'Outsourcing Benefits'
    },
    { 
      text: 'Lack of denial management expertise causing revenue loss',
      link: '/denial-management-services-medical-clinics',
      linkText: 'Denial Management Services'
    },
    { 
      text: 'High overhead costs for in-house billing operations',
      link: '/benefits-medical-billing-outsourcing-clinics',
      linkText: 'Cost Reduction Benefits'
    }
  ];

  return (
    <section
      style={{
        padding: '80px 5%',
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        rowGap: '40px',
        fontFamily: "'Poppins', sans-serif",
      }}
      itemScope
      itemType="https://schema.org/Article"
      role="region"
      aria-label="Medical billing challenges for small practices"
    >
      {/* Hidden image for SEO */}
      <img 
        src={tiredStaff} 
        alt="Small practice medical billing staff overwhelmed by claim denials and manual processes - struggling with healthcare billing efficiency" 
        style={{ display: 'none' }}
        itemProp="image"
      />

      {/* Left Text Content - SEO OPTIMIZED with NEW KEYWORDS */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
        style={{
          flex: '1 1 50%',
          minWidth: '300px',
          paddingRight: isMobile ? '0' : '30px',
          textAlign: isMobile ? 'center' : 'left',
        }}
        itemProp="articleBody"
      >
        {/* ✅ H2 with Primary Keywords */}
        <h2
          style={{
            fontSize: isMobile ? '1.9rem' : '2.6rem',
            fontWeight: '700',
            marginBottom: '20px',
            color: '#1A1A1A',
            lineHeight: '1.3',
          }}
          itemProp="headline"
        >
          Why Small Practices Need Medical Billing Outsourcing
        </h2>

        {/* Supporting paragraph with keywords */}
        <p style={{
          fontSize: '1.05rem',
          lineHeight: '1.7',
          color: '#444',
          marginBottom: '25px',
        }}>
          Small practices, independent physicians, and multispecialty clinics face unique challenges in managing their{' '}
          <Link 
            to="/revenue-cycle-management-independent-physicians" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Learn about revenue cycle management for independent physicians"
          >
            revenue cycle management
          </Link>. Without expert{' '}
          <Link 
            to="/medical-billing-outsourcing-small-practices" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Discover medical billing outsourcing for small practices"
          >
            medical billing outsourcing
          </Link>, many providers struggle with:
        </p>

        <ul
          style={{
            paddingLeft: '0',
            listStyle: 'none',
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: '#333',
            marginLeft: isMobile ? '0' : '10px',
          }}
          itemProp="itemList"
          itemScope
          itemType="https://schema.org/ItemList"
        >
          {problems.map((item, i) => (
            <motion.li
              key={i}
              style={{
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: isMobile ? 'center' : 'flex-start',
                flexDirection: isMobile ? 'column' : 'row',
              }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <span
                style={{
                  color: '#EF4444',
                  fontWeight: 'bold',
                  marginRight: '12px',
                  fontSize: '1.3rem',
                  lineHeight: '1.4',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                ✗
              </span>
              <div style={{ flex: 1 }}>
                <span itemProp="name">{item.text}</span>
                {' '}
                <Link 
                  to={item.link}
                  style={{ 
                    color: '#0077b6', 
                    fontSize: '0.9rem',
                    textDecoration: 'underline',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}
                  aria-label={`Learn more about ${item.linkText}`}
                >
                  → {item.linkText}
                </Link>
              </div>
            </motion.li>
          ))}
        </ul>

        {/* Call-to-action paragraph with internal links */}
        <p style={{
          fontSize: '1rem',
          lineHeight: '1.7',
          color: '#555',
          marginTop: '25px',
          fontStyle: 'italic',
        }}>
          That's why more small practices and independent physicians are choosing{' '}
          <Link 
            to="/medical-billing-outsourcing-small-practices" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Learn about medical billing outsourcing for small practices"
          >
            medical billing outsourcing
          </Link>{' '}
          with expert{' '}
          <Link 
            to="/denial-management-services-medical-clinics" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Explore denial management services for medical clinics"
          >
            denial management services
          </Link>{' '}
          to{' '}
          <Link 
            to="/reduce-claim-denials-healthcare-billing" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            aria-label="Learn how to reduce claim denials in healthcare billing"
          >
            reduce claim denials
          </Link>{' '}
          and maximize revenue.
        </p>

        {/* Additional CTA Box */}
        <div style={{
          marginTop: '30px',
          padding: '25px',
          backgroundColor: '#e8f5e9',
          borderRadius: '12px',
          border: '2px solid #4caf50',
        }}>
          <h3 style={{
            fontSize: '1.2rem',
            fontWeight: '700',
            color: '#2e7d32',
            marginBottom: '15px',
          }}>
            🎯 Ready to Transform Your Billing?
          </h3>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.6',
            color: '#444',
            marginBottom: '15px',
          }}>
            Discover the <strong>benefits of medical billing outsourcing for clinics</strong>. We've helped 500+ small practices reduce costs by 30-50% and{' '}
            <Link 
              to="/reduce-claim-denials-healthcare-billing"
              style={{ color: '#2e7d32', textDecoration: 'underline', fontWeight: '600' }}
            >
              reduce claim denials by 35-45%
            </Link>.
          </p>
          <Link 
            to="/free-trial"
            style={{
              display: 'inline-block',
              padding: '12px 28px',
              backgroundColor: '#2e7d32',
              color: '#ffffff',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1b5e20'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e7d32'}
          >
            Get Free Consultation
          </Link>
        </div>
      </motion.div>

      {/* Right Image */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        style={{
          flex: '1 1 40%',
          minWidth: '250px',
          textAlign: 'center',
          marginTop: isMobile ? '30px' : '0',
        }}
      >
        <img
          src={tiredStaff}
          alt="Small practice billing staff overwhelmed by claim denials and manual healthcare billing processes"
          style={{
            width: '100%',
            maxWidth: '450px',
            borderRadius: '12px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
            filter: 'grayscale(15%)',
          }}
          loading="lazy"
          itemProp="image"
        />
        <p style={{
          fontSize: '0.85rem',
          color: '#666',
          marginTop: '10px',
          fontStyle: 'italic',
        }}>
          Small practice teams overwhelmed by billing -{' '}
          <Link 
            to="/benefits-medical-billing-outsourcing-clinics" 
            style={{ color: '#0077b6' }}
          >
            See the outsourcing solution
          </Link>
        </p>

        {/* Quick Stats Box with NEW KEYWORDS */}
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          textAlign: 'left',
        }}>
          <h4 style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#0077b6',
            marginBottom: '15px',
          }}>
            📊 Small Practice Statistics
          </h4>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.95rem',
            lineHeight: '1.8',
            color: '#555',
          }}>
            <li>• 15-20% average claim denial rate</li>
            <li>• 40-60 day typical payment cycle</li>
            <li>• $35-50/claim processing cost</li>
            <li>• 50% staff time on billing tasks</li>
          </ul>
          <Link 
            to="/medical-billing-outsourcing-small-practices"
            style={{
              display: 'inline-block',
              marginTop: '15px',
              color: '#0077b6',
              textDecoration: 'underline',
              fontSize: '0.9rem',
              fontWeight: '600',
            }}
          >
            See how outsourcing solves this →
          </Link>
        </div>

        {/* Service Links Box */}
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '10px',
          border: '1px solid #e0e0e0',
          textAlign: 'left',
        }}>
          <h4 style={{
            fontSize: '1rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '12px',
          }}>
            🔗 Quick Links by Practice Type
          </h4>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <Link 
              to="/medical-billing-outsourcing-small-practices"
              style={{
                color: '#0077b6',
                fontSize: '0.9rem',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.paddingLeft = '8px'}
              onMouseLeave={(e) => e.currentTarget.style.paddingLeft = '0'}
            >
              → Small Practice Billing
            </Link>
            <Link 
              to="/revenue-cycle-management-independent-physicians"
              style={{
                color: '#0077b6',
                fontSize: '0.9rem',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.paddingLeft = '8px'}
              onMouseLeave={(e) => e.currentTarget.style.paddingLeft = '0'}
            >
              → Independent Physician RCM
            </Link>
            <Link 
              to="/revenue-cycle-management-multispecialty-practices"
              style={{
                color: '#0077b6',
                fontSize: '0.9rem',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.paddingLeft = '8px'}
              onMouseLeave={(e) => e.currentTarget.style.paddingLeft = '0'}
            >
              → Multispecialty Practice Solutions
            </Link>
            <Link 
              to="/denial-management-services-medical-clinics"
              style={{
                color: '#0077b6',
                fontSize: '0.9rem',
                textDecoration: 'none',
                fontWeight: '500',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.paddingLeft = '8px'}
              onMouseLeave={(e) => e.currentTarget.style.paddingLeft = '0'}
            >
              → Denial Management Services
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Mobile Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            section {
              padding: 60px 4%;
            }

            h2 {
              margin-bottom: 15px !important;
            }

            li {
              flex-direction: column !important;
              align-items: flex-start !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default ProblemSection;